/**
 * PURPOSE: Vista Por Grupo del Centro de Programación (FTR-0028 WG3, §15 / vista 1).
 * RESPONSIBILITIES: Mostrar la ESTRUCTURA deportiva — grupos, slots, jornadas, BYE, fases finales y
 *   sus cruces — y dejar claro qué está definido, qué es un partido real y qué sigue pendiente.
 *
 *   ESTA VISTA RESPONDE "QUÉ", NO "CUÁNDO". El horario aparece como resumen de una línea, no como
 *   formulario: quien viene a revisar la estructura no debería tropezar con cuatro campos por fila.
 *   Programar desde aquí se hace con la selección y la barra de acciones, que son las mismas de las
 *   otras tres vistas (D-24).
 *
 *   LAS FASES FINALES NACEN VACÍAS (D-26). "Por definir" no es un dato que falte: es el estado
 *   correcto de una llave cuyos clasificados todavía no existen.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, MatchRow.jsx, utils/programmingCenterGrouping.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/views/GroupView.jsx
 */
import React, { useMemo } from "react";
import { FiLayers, FiTrash2, FiUnlock } from "react-icons/fi";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { groupByGroup, conflictSeverity } from "../../../../utils/programmingCenterGrouping.js";
import { legLabel } from "../../../../utils/programmingCenterModel.js";
import MatchRow from "../components/MatchRow.jsx";
import { Badge } from "../components/StateBadges.jsx";

const BlockHeader = ({ title, subtitle, rows, children }) => {
  const { selection, setBlockSelection } = useProgrammingCenter();
  const selectable = rows.filter((row) => row.canSelect);
  const allSelected = selectable.length > 0 && selectable.every((row) => selection.has(row.iddraftmatch));

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
      <div className="flex items-center gap-3">
        {selectable.length ? (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(event) => setBlockSelection(selectable, event.target.checked)}
            aria-label={`Seleccionar ${title}`}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-400"
          />
        ) : null}
        <div>
          <p className="text-sm font-black text-slate-900">{title}</p>
          {subtitle ? <p className="text-[11px] font-bold text-slate-500">{subtitle}</p> : null}
        </div>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
};

const RowActions = ({ row }) => {
  const { actions } = useProgrammingCenter();

  if (row.isOrphan && row.iddraftmatch) {
    return (
      <button
        type="button"
        title="Convertir en partido manual para que una regeneración no vuelva a dejarlo huérfano"
        onClick={() => actions.convertToManual(row)}
        className="rounded-lg border border-slate-200 p-1.5 text-slate-500 transition hover:border-blue-300 hover:text-blue-600"
      >
        <FiUnlock className="text-sm" />
      </button>
    );
  }
  if (row.canDeleteRow) {
    return (
      <button
        type="button"
        title="Quitar esta fila del borrador"
        onClick={() => actions.deleteRow(row)}
        className="rounded-lg border border-slate-200 p-1.5 text-slate-500 transition hover:border-rose-300 hover:text-rose-600"
      >
        <FiTrash2 className="text-sm" />
      </button>
    );
  }
  if (row.canRetireMatch) {
    return (
      <button
        type="button"
        title="Retirar el partido materializado (solo si no tiene actividad)"
        onClick={() => actions.retireMatch(row)}
        className="rounded-lg border border-slate-200 p-1.5 text-slate-500 transition hover:border-rose-300 hover:text-rose-600"
      >
        <FiTrash2 className="text-sm" />
      </button>
    );
  }
  return null;
};

const Rows = ({ rows }) => {
  const { selection, toggleSelection, conflictsByKey } = useProgrammingCenter();
  return (
    <div className="flex flex-col gap-1.5 p-3">
      {rows.map((row) => {
        const conflicts = conflictsByKey.get(row.draftKey) || [];
        return (
          <MatchRow
            key={row.key}
            row={row}
            layout="group"
            selected={selection.has(row.iddraftmatch)}
            onToggle={toggleSelection}
            conflicts={conflicts}
            severity={conflictSeverity(conflicts)}
            actions={<RowActions row={row} />}
          />
        );
      })}
    </div>
  );
};

const GroupView = () => {
  const { visibleRows } = useProgrammingCenter();
  const projection = useMemo(() => groupByGroup(visibleRows), [visibleRows]);

  if (!visibleRows.length) {
    return (
      <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-bold text-slate-500">
        No hay partidos que coincidan con los filtros actuales.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {projection.groups.map((group) => {
        const materialized = group.rows.filter((row) => row.isMaterialized).length;
        return (
          <section
            key={group.groupNo}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
          >
            <BlockHeader
              title={group.label}
              subtitle={`${group.rows.filter((row) => !row.isBye).length} partidos · ${
                group.rows.filter((row) => row.isBye).length
              } BYE · ${group.dates.length} jornadas`}
              rows={group.rows}
            >
              <Badge tone={materialized ? "green" : "slate"}>
                {materialized}/{group.rows.length} materializados
              </Badge>
            </BlockHeader>

            {group.dates.map((date) => (
              <div key={date.dateNo} className="border-b border-slate-100 last:border-b-0">
                <div className="flex items-center gap-2 bg-slate-50/80 px-3 py-1.5">
                  <FiLayers className="text-xs text-slate-400" />
                  <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                    Fecha {date.dateNo}
                    {date.leg ? ` · ${legLabel(date.leg)}` : ""}
                  </span>
                </div>
                <Rows rows={date.rows} />
              </div>
            ))}
          </section>
        );
      })}

      {projection.finals.length ? (
        <section className="overflow-hidden rounded-2xl border border-violet-200 bg-white shadow-sm">
          <BlockHeader
            title="Fase final"
            subtitle="Las llaves se muestran vacías hasta que sus clasificados existan"
            rows={projection.finals.flatMap((stage) => stage.rows)}
          />
          {projection.finals.map((stage) => (
            <div key={stage.stage} className="border-b border-slate-100 last:border-b-0">
              <div className="bg-violet-50/60 px-3 py-1.5">
                <span className="text-[11px] font-black uppercase tracking-wide text-violet-600">
                  {stage.rows[0]?.tagFinal?.replace(/\s\d+$/, "") || stage.stage}
                </span>
              </div>
              <Rows rows={stage.rows} />
            </div>
          ))}
        </section>
      ) : null}

      {projection.loose.length ? (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <BlockHeader
            title="Sin grupo"
            subtitle="Partidos manuales que cruzan grupos o no pertenecen a ninguno"
            rows={projection.loose}
          />
          <Rows rows={projection.loose} />
        </section>
      ) : null}
    </div>
  );
};

export default GroupView;
